import styled from 'styled-components';
import React, { useState } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import ConfigurableMobileHeader, { MobileHeaderType } from './ConfigurableMobileHeader';
import MobileNavigation from './MobileNavigation';
import UploadModal from '../molecules/UploadModal';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background.main};
  overflow-x: hidden;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding-bottom: 64px; /* Space for mobile navigation */
  }
`;

interface MainContentProps {
  $isExpanded: boolean;
  children: React.ReactNode;
}

const StyledMainContent = styled.main<Omit<MainContentProps, 'children'> & { $hideHeaderOnMobile?: boolean }>`
  margin-left: ${({ theme, $isExpanded }) => 
    $isExpanded ? theme.sizes.sidebar.expanded : theme.sizes.sidebar.collapsed};
  margin-top: ${({ theme }) => theme.sizes.header.height};
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.text.primary};
  transition: margin-left 0.3s ease;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-left: 0;
    margin-top: ${({ $hideHeaderOnMobile }) => $hideHeaderOnMobile ? '0' : 'calc(48px + 48px)'}; /* Adjust based on header visibility */
    padding: 0;
  }
`;

const MainContent: React.FC<Omit<MainContentProps, '$isExpanded'> & { isExpanded: boolean, hideHeaderOnMobile?: boolean }> = ({ isExpanded, children, hideHeaderOnMobile }) => (
  <StyledMainContent $isExpanded={isExpanded} $hideHeaderOnMobile={hideHeaderOnMobile}>
    {children}
  </StyledMainContent>
);

interface LayoutProps {
  children: React.ReactNode;
  hideHeaderOnMobile?: boolean;
  hideSidebarOnMobile?: boolean;
  hideBottomNavOnMobile?: boolean;
  // Props for mobile header configuration
  customMobileHeader?: React.ReactNode;
  mobileHeaderType?: MobileHeaderType;
  mobileHeaderTitle?: string;
  showBackButton?: boolean;
  backPath?: string;
  transparentHeader?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ 
  children,
  hideHeaderOnMobile = false,
  hideSidebarOnMobile = false,
  hideBottomNavOnMobile = false,
  customMobileHeader = null,
  mobileHeaderType = MobileHeaderType.FULL,
  mobileHeaderTitle,
  showBackButton = false,
  backPath,
  transparentHeader = false
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'enterprises' | 'trending' | 'insights'>('enterprises');

  const handleSidebarToggle = (expanded: boolean) => {
    setIsExpanded(expanded);
  };
  
  const handleOpenUploadModal = () => {
    setIsUploadModalOpen(true);
  };
  
  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
  };
  
  const handleTabChange = (tab: 'enterprises' | 'trending' | 'insights') => {
    setActiveTab(tab);
  };

  return (
    <LayoutContainer>
      {/* Desktop Components */}
      {!hideSidebarOnMobile && <Sidebar onToggle={handleSidebarToggle} />}
      <Header isExpanded={isExpanded} hideOnMobile={hideHeaderOnMobile} />
      
      {/* Mobile Components - conditionally render based on props */}
      {customMobileHeader ? (
        // Render custom mobile header if provided
        <>{customMobileHeader}</>
      ) : (
        // Otherwise render configurable mobile header based on props
        <ConfigurableMobileHeader 
          type={mobileHeaderType}
          title={mobileHeaderTitle}
          showBackButton={showBackButton}
          backPath={backPath}
          transparent={transparentHeader}
          hideOnMobile={hideHeaderOnMobile}
          activeTab={activeTab} 
          onTabChange={handleTabChange}
        />
      )}
      <MobileNavigation 
        onUploadClick={handleOpenUploadModal}
        hideOnMobile={hideBottomNavOnMobile}
      />
      
      <MainContent isExpanded={isExpanded} hideHeaderOnMobile={hideHeaderOnMobile}>
        {children}
      </MainContent>
      
      <UploadModal 
        isOpen={isUploadModalOpen} 
        onClose={handleCloseUploadModal} 
      />
    </LayoutContainer>
  );
};

export default Layout;
